import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { TVideoAsset, TVideoType, TVimeoVideoMetadata } from '@/types/global';
import {
  CSSProperties,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import Player from '@vimeo/player';
import { VimeoVideo } from '../VimeoVideo/VimeoVideo';
import * as Styled from './Video.styled';

gsap.registerPlugin(ScrollTrigger);

export interface IVideoProps extends TVideoAsset {
  width?: number;
  height?: number;
  controls?: boolean;
  autoplay?: 'always' | 'whenVisible' | 'never';
  videoState?: 'paused' | 'play' | 'unset';
  includesSound?: boolean;
  vimeoSizeLimit?: number;
  loop?: boolean;
  type?: TVideoType;
  fill?: boolean;
  playsOnLoad?: boolean;
  includeContainer?: boolean;
  includesControls?: boolean;
  includeInSlider?: boolean;
  aspectRatio?: string;
  vimeoId?: string;
  styles?: CSSProperties;
  ref?: React.Ref<HTMLVideoElement>;
  vimeoMetadata?: TVimeoVideoMetadata | undefined;
  classNameContainer?: string;
}

export const Video = ({
  autoplay = 'whenVisible',
  vimeoId = '',
  includeContainer = false,
  includesSound,
  includesControls,
  aspectRatio = '16 / 9',
  // playsOnLoad,
  // vimeoSizeLimit,
  vimeoMetadata,
  poster,
  title,
  includeInSlider = false,
  classNameContainer
}: IVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const refVimeoVideoContainer = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<Player | null>(null);

  useEffect(() => {
    if (!iframeRef.current || playerRef.current) return;
    playerRef.current = new Player(iframeRef.current);
  }, []);

  // ScrollTrigger for native HTML5 video
  useGSAP(() => {
    if (!videoRef.current || autoplay !== 'whenVisible' || vimeoId) return;
    const video = videoRef.current;

    const playPauseTrigger = ScrollTrigger.create({
      trigger: video,
      start: 'top bottom',
      end: 'bottom top',
      onEnter: () => video.play(),
      onEnterBack: () => video.play(),
      onLeave: () => video.pause(),
      onLeaveBack: () => video.pause(),
    });

    const preloadTrigger = ScrollTrigger.create({
      trigger: video,
      start: `top+=${window.innerHeight / 2} bottom`,
      end: `bottom-=${window.innerHeight / 2} top`,
      once: true,
      onEnter: () => {
        if (video.getAttribute('preload') !== 'auto') {
          video.setAttribute('preload', 'auto');
          video.load();
        }
      },
    });

    return () => {
      playPauseTrigger.kill();
      preloadTrigger.kill();
    };
  }, [autoplay, vimeoId]);

  useLayoutEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          player.getFullscreen();
          player.play().catch((err: any) => {
            console.warn('Vimeo play error:', err);
          });
        } else {
          player.pause().catch((err: any) => {
            console.warn('Vimeo pause error:', err);
          });
        }
      },
      {
        root: null,
        threshold: 0.1,
      }
    );

    if (refVimeoVideoContainer.current)
      observer.observe(refVimeoVideoContainer.current);
  });

  const VideoElement = () =>
    vimeoId ? (
      <VimeoVideo
        vimeoId={vimeoId}
        includesControls={includesControls}
        includesSound={includesSound}
        // vimeoSizeLimit={vimeoSizeLimit}
        // playsOnLoad={playsOnLoad}
        autoplay={autoplay}
        title={title}
        includeInSlider={includeInSlider}
        classNameContainer={classNameContainer}
        poster={poster}
        {...(aspectRatio && { aspectRatio })}
        {...(vimeoMetadata && { vimeoMetadata })}      />
    ) : (
      <></>
    );

  return includeContainer ? (
    <Styled.VideoContainer aspectRatio={aspectRatio || ''} className='video-container'>
      <VideoElement />
    </Styled.VideoContainer>
  ) : (
    <>
      <VideoElement />
    </>
  );
};
